import { db } from './firebase';
import { Settings } from "../types";

// Generic listener for any collection per user
export const listenToCollection = (userId: string, collectionName: string, callback: (data: any[]) => void) => {
    if (!db) {
        console.error("Firestore not initialized");
        return () => {};
    }
    const collectionRef = db.collection(`users/${userId}/${collectionName}`);
    const unsubscribe = collectionRef.onSnapshot((querySnapshot) => {
        const dataList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        callback(dataList);
    });
    return unsubscribe;
};

// Add a new document to a user's sub-collection
export const addDocument = async (userId: string, collectionName: string, data: object) => {
    if (!db) throw new Error("Firestore not initialized");
    await db.collection(`users/${userId}/${collectionName}`).add(data);
};

// Add multiple documents in a single batch operation
export const addMultipleDocuments = async (userId: string, collectionName: string, data: object[]) => {
    if (!db) throw new Error("Firestore not initialized");
    const batch = db.batch();
    const collectionRef = db.collection(`users/${userId}/${collectionName}`);
    data.forEach(item => {
        const docRef = collectionRef.doc(); // Automatically generate a new ID
        batch.set(docRef, item);
    });
    await batch.commit();
};


// Update an existing document in a user's sub-collection
export const updateDocument = async (userId: string, collectionName: string, docId: string, data: object) => {
    if (!db) throw new Error("Firestore not initialized");
    const docRef = db.doc(`users/${userId}/${collectionName}/${docId}`);
    await docRef.update(data);
};

// Delete a document from a user's sub-collection
export const deleteDocument = async (userId: string, collectionName: string, docId: string) => {
    if (!db) throw new Error("Firestore not initialized");
    await db.doc(`users/${userId}/${collectionName}/${docId}`).delete();
};


// --- Settings (Categories & Rules) Management ---

// Listen to the user's settings document
export const listenToSettings = (userId: string, callback: (settings: Settings) => void, defaultSettings: Settings) => {
    if (!db) {
        console.error("Firestore not initialized");
        return () => {};
    }
    const settingsDocRef = db.doc(`users/${userId}/settings/main`);
    
    const unsubscribe = settingsDocRef.onSnapshot(async (docSnap) => {
        if (docSnap.exists) {
            const data = docSnap.data()!;
            const settings: Settings = {
                categories: data.categories || defaultSettings.categories,
                rules: data.rules || defaultSettings.rules,
                prorationRules: data.prorationRules || [],
                currency: data.currency || defaultSettings.currency,
                onboardingCompleted: data.onboardingCompleted || false,
                showTourGuide: data.showTourGuide !== undefined ? data.showTourGuide : true,
                subscriptionStatus: data.subscriptionStatus || 'free',
                trialEndDate: data.trialEndDate || undefined,
                subscriptionEndDate: data.subscriptionEndDate || undefined,
                stripeCustomerId: data.stripeCustomerId || undefined,
            };
            callback(settings);
        } else {
            // If settings don't exist, create them with defaults (but onboarding not completed)
            console.log(`Creating default settings for new user ${userId}`);
            // FIX: Explicitly type `initialSettings` as `Settings` to ensure `subscriptionStatus` is not widened to `string`.
            const initialSettings: Settings = { ...defaultSettings, onboardingCompleted: false, showTourGuide: true, subscriptionStatus: 'free' };
            await settingsDocRef.set(initialSettings);
            callback(initialSettings);
        }
    });
    return unsubscribe;
};

// Update the user's settings document
export const updateSettings = async (userId: string, newSettings: Partial<Settings>) => {
    if (!db) throw new Error("Firestore not initialized");
    const settingsDocRef = db.doc(`users/${userId}/settings/main`);
    await settingsDocRef.set(newSettings, { merge: true });
};


// --- Account Deletion & Feedback ---

export const deleteUserData = async (userId: string): Promise<void> => {
    if (!db) throw new Error("Firestore not initialized");
    
    const batch = db.batch();

    // Delete all transactions
    const transactionsRef = db.collection(`users/${userId}/transactions`);
    const transactionsSnap = await transactionsRef.get();
    transactionsSnap.forEach(doc => batch.delete(doc.ref));

    // Delete all time entries
    const timeEntriesRef = db.collection(`users/${userId}/timeEntries`);
    const timeEntriesSnap = await timeEntriesRef.get();
    timeEntriesSnap.forEach(doc => batch.delete(doc.ref));

    // Delete the settings document
    const settingsDocRef = db.doc(`users/${userId}/settings/main`);
    batch.delete(settingsDocRef);
    
    await batch.commit();
};

export const saveFeedback = async (feedback: { reason: string; comments: string }): Promise<void> => {
    if (!db) throw new Error("Firestore not initialized");
    const feedbackCollection = db.collection('feedback');
    await feedbackCollection.add({
        ...feedback,
        timestamp: new Date().toISOString()
    });
};